import type { SVGProps } from 'react';

/**
 * Hand-rolled 24px icon set. An icon font or an icon package would be a
 * dependency and a network payload for the sake of a dozen glyphs.
 */
type IconProps = SVGProps<SVGSVGElement>;

function Svg({ children, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="size-6"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const HomeIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.8V20h14V9.8" />
    <path d="M9.5 20v-5.5h5V20" />
  </Svg>
);

export const BallIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="m12 7.5 3.2 2.3-1.2 3.8h-4l-1.2-3.8Z" />
    <path d="M12 3v4.5M4.2 9.6l4 .2M19.8 9.6l-4 .2M7.1 19.6l1.9-3.5M16.9 19.6 15 16.1" />
  </Svg>
);

export const ListIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M8 6h13M8 12h13M8 18h13" />
    <path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
  </Svg>
);

export const WalletIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a2 2 0 0 1 2 2v1.5" />
    <rect x="3" y="7.5" width="18" height="11.5" rx="2.5" />
    <path d="M16.5 13.2h.01" />
  </Svg>
);

export const SettingsIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.5v2.2M12 19.3v2.2M4.2 7.2l1.9 1.1M17.9 15.7l1.9 1.1M4.2 16.8l1.9-1.1M17.9 8.3l1.9-1.1" />
  </Svg>
);

export const TrophyIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M7 4h10v4a5 5 0 0 1-10 0Z" />
    <path d="M7 5.5H4.5V8A3.5 3.5 0 0 0 8 11.5M17 5.5h2.5V8A3.5 3.5 0 0 1 16 11.5" />
    <path d="M12 13v4M9 20h6" />
  </Svg>
);

export const CrownIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m3.5 7 4.5 4 4-6 4 6 4.5-4-2 11h-13Z" />
    <path d="M6 18h12" />
  </Svg>
);

export const ShieldIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 3l7 2.5v5.8c0 4.2-2.8 7.6-7 9.2-4.2-1.6-7-5-7-9.2V5.5Z" />
    <path d="m9 12 2.2 2.2L15.5 10" />
  </Svg>
);

export const UsersIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="9.5" cy="8" r="3.2" />
    <path d="M3.5 19.5c0-3 2.7-5 6-5s6 2 6 5" />
    <path d="M16.2 5.2a3 3 0 0 1 0 5.8M17.5 14.9c1.9.6 3 2.2 3 4.6" />
  </Svg>
);

export const CoinsIcon = (props: IconProps) => (
  <Svg {...props}>
    <ellipse cx="9" cy="6.5" rx="5.5" ry="2.5" />
    <path d="M3.5 6.5v4c0 1.4 2.5 2.5 5.5 2.5s5.5-1.1 5.5-2.5v-4" />
    <path d="M9 17.5c-3 0-5.5-1.1-5.5-2.5" />
    <ellipse cx="15" cy="15.5" rx="5.5" ry="2.5" />
    <path d="M9.5 15.5v2c0 1.4 2.5 2.5 5.5 2.5s5.5-1.1 5.5-2.5v-2" />
  </Svg>
);

export const CrystalIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m7 4-4 6 9 10 9-10-4-6Z" />
    <path d="m3 10 4-2 5 12M21 10l-4-2-5 12M7 4l5 4 5-4M7 8h10" />
  </Svg>
);

export const CalendarIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
    <path d="M3.5 9.5h17M8.5 3.5V6M15.5 3.5V6" />
  </Svg>
);

export const ClipboardIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M9 4.5h6M8 6.5H6.5A1.5 1.5 0 0 0 5 8v11a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V8a1.5 1.5 0 0 0-1.5-1.5H16" />
    <rect x="8" y="3" width="8" height="3.5" rx="1.2" />
    <path d="M8.5 11h7M8.5 15h4.5" />
  </Svg>
);

export const ReverseIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 10a8 8 0 0 1 13.7-4.4L20 8" />
    <path d="M20 4.5V8h-3.5" />
    <path d="M20 14a8 8 0 0 1-13.7 4.4L4 16" />
    <path d="M4 19.5V16h3.5" />
  </Svg>
);

export const LogoutIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M14.5 4.5h3A2 2 0 0 1 19.5 6.5v11a2 2 0 0 1-2 2h-3" />
    <path d="M11 8 7 12l4 4M7 12h9" />
  </Svg>
);

export const MenuIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Svg>
);

export const PlusIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const MinusIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M5 12h14" />
  </Svg>
);

export const HistoryIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3.5 12a8.5 8.5 0 1 0 2.5-6" />
    <path d="M3.5 4v4h4" />
    <path d="M12 7.5V12l3 2" />
  </Svg>
);

export const WarningIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 4.5 21 19.5H3Z" />
    <path d="M12 10v4M12 16.8h.01" />
  </Svg>
);

export const EyeIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);

export const LockIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </Svg>
);

export const CheckIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m5 12 5 5 9-10" />
  </Svg>
);
